/* eslint-disable react-hooks/rules-of-hooks */
import { B2CCartProcessTile } from "~/features/cart/B2CCart/components/B2CCartProcessTile/B2CCartProcessTile";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import { SIZE_DESKTOP_S, SIZE_PHONE, SIZE_TABLET, useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { Button, IconButton, styled } from "~/common/mui";
import ClearCartButton, { useClearCartAction } from "~/features/cart/cart/components/Buttons/ClearCartButton";
import { EnterDesignationBtn } from "~/features/cart/cart/components/Buttons/EnterDesignationButton";
import { canSeeWishlistButton } from "~/utils/features";
import AddCartToWishlistBtn from "~/features/cart/cart/components/Buttons/AddCartToWishlistBtn";
import { useEffect, useRef, useState } from "react";
import { B2CProductList } from "~/features/cart/B2CCart/components/B2CProductList/B2CProductList";
import { useDispatch, useSelector } from "react-redux";
import { type LineItem } from "~/features/cart/cart/models/CartData";
import { type ProductData } from "~/domains/product/types/ProductData";
import { type RootState } from "~/app/store";
import { deletePackageEntry, updatePackageEntry } from "~/features/cart/cart/redux/actions/cartQuoteCommonActions";
import { updateCartCommentThunk } from "~/features/cart/cart/redux/actions/cartPageActions";
import { useTranslation } from "react-i18next";
import { CART_STEP_2 } from "~/utils/url/staticUrls";
import { resolveUrl } from "~/utils/url/urlResolver";
import { CommentTargets, trackCommentActions } from "~/app/analytics/ga/salesPartner";
import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { CommentButton } from "~/common/components/CommentButton/CommentButton";
import { CommentButton as LabeledCommentButton } from "~/pages/WishlistPage/components/buttons/CommentButton";
import DeleteIcon from "@mui/icons-material/Delete";

const Root = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(4),
}));

const HeaderSection = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplate: `"title phoneActions" auto "leftActions leftActions" auto / 1fr 1fr`,
  [theme.breakpoints.up("tablet")]: {
    gridTemplate: `"title title" auto "leftActions leftActions" auto "rightActions rightActions" auto / 1fr 1fr`,
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridTemplate: `"title title" auto "leftActions rightActions" auto / auto 1fr`,
  },
}));

const PageTitleSlot = styled(PageTitle)({ gridArea: "title" });

const PhoneActions = styled("div")({
  gridArea: "phoneActions",
  justifySelf: "flex-end",
});

const LeftActions = styled("div")(({ theme }) => ({
  gridArea: "leftActions",
  justifySelf: "flex-start",
  display: "flex",
  gap: theme.spacing(2),
  [theme.breakpoints.up("tablet")]: {
    gap: theme.spacing(3),
  },
}));

const RightActions = styled("div")(({ theme }) => ({
  gridArea: "rightActions",
  justifySelf: "flex-end",
  "& > *": {
    marginTop: theme.spacing(-1),
    [theme.breakpoints.up("desktop-s")]: {
      marginTop: 0,
    },
  },
}));

const CartComment = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(-2),
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(1),
  width: "100%",
  [theme.breakpoints.up("desktop-s")]: {
    width: 636,
  },
  [theme.breakpoints.up("desktop-m")]: {
    width: 732,
  },
  [theme.breakpoints.up("desktop-l")]: {
    width: 880,
  },
}));

const BottomContinue = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
});

const PhoneContinue = styled("div")(({ theme }) => ({
  position: "sticky",
  bottom: -1,
  padding: `${theme.spacing(2)} 0`,
  zIndex: 1,
  "& > *": {
    width: "100%",
  },
}));

const PhoneContinuePinned = styled("div")(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  marginLeft: theme.spacing(-2),
  marginRight: theme.spacing(-2),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  background: theme.palette.common.white,
  zIndex: 1,
}));

interface Props {
  className?: string;
}

export const B2CCartPage = (props: Props) => {
  const { t } = useTranslation();
  const clearCart = useClearCartAction();

  const size = useNumericScreenSize();
  const isPhone = size <= SIZE_PHONE;
  const isMinTablet = size >= SIZE_TABLET;
  const isDesktop = size >= SIZE_DESKTOP_S;

  const [pinnedElRef, isPinned] = usIsPinnedDetector<HTMLDivElement>();

  const continueButton = <Button href={resolveUrl(CART_STEP_2)}>{t("page.cart.b2c.btn.continue")}</Button>;

  const items = useSelector(selectCartElements);
  const dispatch = useDispatch();
  const updateItem = (item: LineItem, changes: Partial<LineItem>) => {
    if (Object.entries(changes).flat()[0] === "comment") {
      trackCommentActions(
        changes.comment,
        item.comment,
        CommentTargets.CART_ITEM,
        changes?.comment?.length,
        item.productCode,
      );
    }
    dispatch(updatePackageEntry(item.id, changes));
  };
  const deleteItem = (item: LineItem) => {
    dispatch(deletePackageEntry(item.id));
  };

  const cartComment = useSelector(selectCartComment);
  const setCommentInstant = (comment: string) => dispatch(updateCartCommentThunk(comment, cartComment));

  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const toggleCommentOpen = () => setIsCommentOpen((prev) => !prev);
  const [commentRef, toggleComment] = useShowAndScrollInput(toggleCommentOpen);

  const PhoneContinueWrapper = isPinned ? PhoneContinuePinned : PhoneContinue;

  return (
    <Root className={props.className}>
      <B2CCartProcessTile activeIndex={0} />
      <HeaderSection>
        <PageTitleSlot>{t("page.cart.b2c.title")}</PageTitleSlot>
        {isPhone && (
          <PhoneActions>
            <CommentButton open={isCommentOpen} onClick={toggleComment} fontSize={"small"} />
            <IconButton onClick={clearCart} color={"primary"}>
              <DeleteIcon  />
            </IconButton>
          </PhoneActions>
        )}
        <LeftActions>
          <EnterDesignationBtn />
          {canSeeWishlistButton() && <AddCartToWishlistBtn />}
          {isMinTablet && <ClearCartButton />}
          {isMinTablet && (
            <LabeledCommentButton isOpen={isCommentOpen} onClick={toggleComment} hasComment={!!cartComment} />
          )}
        </LeftActions>
        <RightActions className="gtm-begin-checkout" hidden={!isMinTablet}>
          {continueButton}
        </RightActions>
      </HeaderSection>
      <CartComment hidden={!isCommentOpen}>
        <CommentInput
          value={cartComment}
          onChange={setCommentInstant}
          label={t("page.cart.b2c.comment.title")}
          ref={commentRef}
        />
      </CartComment>

      <B2CProductList items={items} onDeleteAll={clearCart} onChange={updateItem} onDelete={deleteItem} />

      <BottomContinue hidden={!isMinTablet}>
        {continueButton}
      </BottomContinue>

      <PhoneContinueWrapper
        ref={pinnedElRef}
        hidden={isMinTablet}
      >
        {continueButton}
      </PhoneContinueWrapper>
    </Root>
  );
};

const usIsPinnedDetector = <T extends HTMLElement>() => {
  const elRef = useRef<T>(null);
  const [isPinned, setIsPinned] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => setIsPinned(e.intersectionRatio < 1), { threshold: [1] });
    observer.observe(elRef.current!);

    return () => {
      observer.disconnect();
    };
  }, []);

  return [elRef, isPinned] as const;
};

interface CartElement {
  data: LineItem;
  product: ProductData;
}

const selectCartElements = (root: RootState) => {
  const cart = root.cart;
  const lineItems = Object.values(cart.packageEntriesById);

  const cartElements: CartElement[] = lineItems.map((lineItem) => ({
    data: lineItem,
    product: root.products.basic[lineItem.productCode],
  }));
  return cartElements;
};

const selectCartComment = (root: RootState) => root.cart.comment;
